import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Edit2, Calendar, Layers, Users, KanbanSquare, GanttChartSquare } from 'lucide-react'
import { useProjectStore } from '../../../store/projectStore'
import { usePermissions } from '../../../hooks/usePermissions'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import ProjectFormModal from '../../../components/projects/ProjectFormModal'
import ProjectMemberPicker from '../../../components/projects/ProjectMemberPicker'
import KanbanBoard from '../../../components/projects/KanbanBoard'
import GanttChart from '../../../components/projects/GanttChart'

type Tab = 'overview' | 'board' | 'gantt' | 'members'

const STATUS_STYLES: Record<string, string> = {
  planning: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  on_hold: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
}

const TABS: Array<{ id: Tab; label: string; icon: React.ElementType }> = [
  { id: 'overview', label: 'Overview', icon: Layers },
  { id: 'board', label: 'Board', icon: KanbanSquare },
  { id: 'gantt', label: 'Gantt', icon: GanttChartSquare },
  { id: 'members', label: 'Members', icon: Users },
]

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const projectId = Number(id)
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (searchParams.get('tab') as Tab) || 'overview'
  const { currentProject, fetchProject, isLoading } = useProjectStore()
  const { can } = usePermissions()
  const [editOpen, setEditOpen] = useState(false)

  useEffect(() => {
    if (projectId) fetchProject(projectId)
  }, [projectId, fetchProject])

  if (isLoading && !currentProject) return <LoadingSpinner />
  if (!currentProject || currentProject.id !== projectId) {
    return (
      <div className="p-6 text-center text-gray-500">
        Project not found. <Link to="/admin/projects" className="text-primary-600 hover:underline">Back to list</Link>
      </div>
    )
  }

  const canEdit = can('admin_project_monitoring', 'edit') || can('pwa_project_monitoring', 'edit')

  const setTab = (next: Tab) => setSearchParams({ tab: next })

  return (
    <div className="space-y-4">
      <Link to="/admin/projects" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" />
        Projects
      </Link>

      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2 min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: currentProject.color || '#3b82f6' }} />
            <h1 className="text-xl font-semibold text-gray-900 truncate">{currentProject.name}</h1>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[currentProject.status] || ''}`}>
              {currentProject.status.replace('_', ' ')}
            </span>
          </div>
          {currentProject.description && (
            <p className="text-sm text-gray-600">{currentProject.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
            {currentProject.start_date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                Start: {new Date(currentProject.start_date).toLocaleDateString()}
              </span>
            )}
            {currentProject.end_date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                End: {new Date(currentProject.end_date).toLocaleDateString()}
              </span>
            )}
            {currentProject.owner_name && (
              <span>Owner: <span className="font-medium text-gray-700">{currentProject.owner_name}</span></span>
            )}
          </div>
        </div>

        {canEdit && (
          <button
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
          >
            <Edit2 className="h-4 w-4" />
            Edit
          </button>
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon
            const active = tab === t.id
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            )
          })}
        </nav>
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">Tasks</p>
            <p className="text-2xl font-semibold text-gray-900">{currentProject.task_count ?? 0}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">Members</p>
            <p className="text-2xl font-semibold text-gray-900">{currentProject.member_count ?? 0}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">Status</p>
            <p className="text-2xl font-semibold capitalize text-gray-900">{currentProject.status.replace('_', ' ')}</p>
          </div>
        </div>
      )}

      {tab === 'board' && <KanbanBoard projectId={projectId} />}
      {tab === 'gantt' && <GanttChart projectId={projectId} />}
      {tab === 'members' && <ProjectMemberPicker project={currentProject} canEdit={canEdit} />}

      <ProjectFormModal
        isOpen={editOpen}
        project={currentProject}
        onClose={() => setEditOpen(false)}
        onSaved={() => fetchProject(projectId)}
      />
    </div>
  )
}
